/**
 * Pure recurring catch-up scheduler.
 *
 * Given a list of recurring definitions and `today`, walk forward from each
 * def's `(max(last_generated_date+1, start_date), today]` window and emit
 * one generated transaction per matching date. Determinism guarantee: this
 * module never reads `Date.now()` or `new Date()` — `today` is always
 * passed in by the caller (App.tsx mounts and visibilitychange handlers).
 *
 * Algorithm matches `design.md` "Recurring 用 client-side catch-up" section.
 *
 * Validation: monthly freq_day must be 1-31, weekly 0-6 (Sun=0). Anything
 * else throws `InvalidFreqDayError` early — silently skipping bad defs
 * would hide configuration mistakes downstream.
 */

import type { IsoDate, TxKind } from '@/lib/sheets/types';

/**
 * Recurring definition consumed by the scheduler.
 *
 * Discriminated union by `freq` — `freq_day` semantics depend on `freq`:
 *   - monthly: 1..31 (clamped to month-end at runtime)
 *   - weekly:  0..6 (Sun=0)
 *   - daily / yearly: ignored (kept for round-trip with sheet rows)
 *
 * `{freq:'weekly', freq_day:15}` no longer compiles. The runtime
 * `validate()` guard remains as a safety net for malformed sheet data
 * that bypasses the type system at the parse boundary.
 */
interface RecurringDefBase {
  id: string;
  book_id: string;
  title: string;
  amount_minor: number;
  currency: string;
  kind: TxKind;
  category_id: string;
  subcategory_id?: string;
  note?: string;
  start_date: IsoDate;
  end_date?: IsoDate;
  last_generated_date?: IsoDate;
  active: boolean;
}

/** 1..31 — every day-of-month a monthly recurring can target. */
export type MonthlyFreqDay =
  | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10
  | 11 | 12 | 13 | 14 | 15 | 16 | 17 | 18 | 19 | 20
  | 21 | 22 | 23 | 24 | 25 | 26 | 27 | 28 | 29 | 30 | 31;

/** 0..6 — Sun..Sat for weekly recurrings. */
export type WeeklyFreqDay = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export interface MonthlyDef extends RecurringDefBase {
  freq: 'monthly';
  freq_day: MonthlyFreqDay;
}
export interface WeeklyDef extends RecurringDefBase {
  freq: 'weekly';
  freq_day: WeeklyFreqDay;
}
export interface DailyDef extends RecurringDefBase {
  freq: 'daily';
  /** Ignored. Stored as 0 by convention. */
  freq_day: 0;
}
export interface YearlyDef extends RecurringDefBase {
  freq: 'yearly';
  /** Ignored. Stored as 0 by convention. */
  freq_day: 0;
}

export type RecurringDef = MonthlyDef | WeeklyDef | DailyDef | YearlyDef;

export interface GeneratedTx {
  recurring_id: string;
  book_id: string;
  date: IsoDate;
  amount_minor: number;
  currency: string;
  kind: TxKind;
  category_id: string;
  subcategory_id: string;
  note: string;
}

export interface RecurringNotification {
  kind: 'recurring_generated';
  payload: { recurring_id: string; generated_count: number };
}

export interface CatchUpResult {
  generated: GeneratedTx[];
  notifications: RecurringNotification[];
  /** Map of recurring_id → today's ISO date, only for defs that were processed. */
  nextLastGeneratedDate: Record<string, IsoDate>;
}

export class InvalidFreqDayError extends Error {
  constructor(def: RecurringDef) {
    super(
      `Invalid freq_day=${def.freq_day} for freq=${def.freq} (recurring id=${def.id})`,
    );
    this.name = 'InvalidFreqDayError';
  }
}

/**
 * Date math here is intentionally LOCAL — the rest of the app stores
 * `IsoDate` as `YYYY-MM-DD` interpreted in the user's local zone
 * (`AddTxScreen.todayYmd()` and `start_date` inputs both use local
 * `getFullYear/Month/Date`). Mixing UTC here would make a Taiwan user
 * miss their `freq_day=5` recurring every day until 08:00 local —
 * because at 00:20 local the UTC clock is still on the previous day.
 */

/** Format a Date as YYYY-MM-DD using its local components. */
function fmt(d: Date): IsoDate {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Parse a YYYY-MM-DD string into a local Date at midnight. */
function parseDate(iso: IsoDate): Date {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y!, (m ?? 1) - 1, d ?? 1);
}

/** Number of days in the given month (year, month-0-indexed), local. */
function daysInMonth(year: number, monthZeroIndexed: number): number {
  // Day 0 of next month == last day of current month.
  return new Date(year, monthZeroIndexed + 1, 0).getDate();
}

function addDays(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

function validate(def: RecurringDef): void {
  if (def.freq === 'monthly') {
    if (!Number.isInteger(def.freq_day) || def.freq_day < 1 || def.freq_day > 31) {
      throw new InvalidFreqDayError(def);
    }
  } else if (def.freq === 'weekly') {
    if (!Number.isInteger(def.freq_day) || def.freq_day < 0 || def.freq_day > 6) {
      throw new InvalidFreqDayError(def);
    }
  }
  // daily / yearly: freq_day is ignored, no validation needed.
}

/**
 * Decide whether `cursor` matches a def's frequency rule.
 *
 * monthly: day-of-month equals `freq_day`, clamped to that month's max
 *          (so freq_day=31 matches Feb 28, Apr 30, etc.)
 * weekly:  cursor's day-of-week equals freq_day
 * yearly:  cursor's month + day equals start_date's month + day
 * daily:   always matches
 */
function matches(def: RecurringDef, cursor: Date, start: Date): boolean {
  if (def.freq === 'daily') return true;
  if (def.freq === 'weekly') return cursor.getDay() === def.freq_day;
  if (def.freq === 'monthly') {
    const max = daysInMonth(cursor.getFullYear(), cursor.getMonth());
    const target = Math.min(def.freq_day, max);
    return cursor.getDate() === target;
  }
  // yearly
  return (
    cursor.getMonth() === start.getMonth() &&
    cursor.getDate() === start.getDate()
  );
}

export function catchUp(defs: RecurringDef[], today: Date): CatchUpResult {
  // Truncate to local-midnight so the cursor comparison (also local-midnight
  // dates from parseDate) is apples-to-apples.
  const todayLocal = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  const todayIso = fmt(todayLocal);

  const generated: GeneratedTx[] = [];
  const notifications: RecurringNotification[] = [];
  const nextLastGeneratedDate: Record<string, IsoDate> = {};

  for (const def of defs) {
    if (!def.active) continue;
    validate(def);

    const startDate = parseDate(def.start_date);
    const endDate = def.end_date ? parseDate(def.end_date) : null;
    const lastGen = def.last_generated_date
      ? parseDate(def.last_generated_date)
      : null;

    // Cursor begins at max(last_generated_date+1, start_date)
    let cursor: Date;
    if (lastGen && lastGen.getTime() >= startDate.getTime()) {
      cursor = addDays(lastGen, 1);
    } else {
      cursor = startDate;
    }

    let countForDef = 0;
    while (cursor.getTime() <= todayLocal.getTime()) {
      if (endDate && cursor.getTime() >= endDate.getTime()) break;
      if (matches(def, cursor, startDate)) {
        generated.push({
          recurring_id: def.id,
          book_id: def.book_id,
          date: fmt(cursor),
          amount_minor: def.amount_minor,
          currency: def.currency,
          kind: def.kind,
          category_id: def.category_id,
          subcategory_id: def.subcategory_id ?? '',
          note: def.note ?? '',
        });
        countForDef++;
      }
      cursor = addDays(cursor, 1);
    }

    nextLastGeneratedDate[def.id] = todayIso;
    if (countForDef > 0) {
      notifications.push({
        kind: 'recurring_generated',
        payload: { recurring_id: def.id, generated_count: countForDef },
      });
    }
  }

  return { generated, notifications, nextLastGeneratedDate };
}
